import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IAddress,
  ICreateAddressPayload,
  IDeleteAddressPayload,
  IAddressListParams,
  IAddressSuggestion,
  IAddressSuggestionsParams,
  IUpdateBySellerAddressPayload,
  IUpdateAddressPayload,
} from 'entities/Address/Address.models';
import { addressesTransport } from 'entities/Address/Address.transport';

interface IState {
  addressList: IAddress[];
  addressCount: number;
  address: IAddress | null;
  isAddressLoading: boolean;
  addressSuggestions: IAddressSuggestion[];
}

const initialState: IState = {
  addressList: [],
  addressCount: 0,
  address: null,
  isAddressLoading: false,
  addressSuggestions: [],
};

export const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    setAddressList(state, action: PayloadAction<IAddress[]>) {
      state.addressList = action.payload;
    },
    setAddressCount(state, action: PayloadAction<number>) {
      state.addressCount = action.payload;
    },
    setAddress(state, action: PayloadAction<IAddress>) {
      state.address = action.payload;
    },
    setIsAddressLoading(state, action: PayloadAction<boolean>) {
      state.isAddressLoading = action.payload;
    },
    addAddressListItem(state, action: PayloadAction<IAddress>) {
      state.addressList.push(action.payload);
    },
    patchAddressListItem(state, action: PayloadAction<IAddress>) {
      const address = state.addressList.find((item) => item.id === action.payload.id);

      if (!address) {
        return;
      }

      Object.assign(address, action.payload);
    },
    removeAddressListItem(state, action: PayloadAction<number>) {
      state.addressList = state.addressList.filter((item) => item.id !== action.payload);
    },
    setAddressSuggestions(state, action: PayloadAction<IAddressSuggestion[]>) {
      state.addressSuggestions = action.payload;
    },
  },
});

export const {
  setAddressList,
  setAddressCount,
  setAddress,
  setIsAddressLoading,
  addAddressListItem,
  patchAddressListItem,
  removeAddressListItem,
  setAddressSuggestions,
} = addressSlice.actions;

export const selectAddress = (state: RootState) => state.address.address;
export const selectAddressList = (state: RootState) => state.address.addressList;
export const selectAddressCount = (state: RootState) => state.address.addressCount;
export const selectIsAddressLoading = (state: RootState) => state.address.isAddressLoading;
export const selectAddressSuggestions = (state: RootState) => state.address.addressSuggestions;

export const addressReducer = addressSlice.reducer;

export const getAddressList = (params: IAddressListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsAddressLoading(true));

  try {
    const response = await addressesTransport.get(params);
    dispatch(setAddressList(response.data));
    dispatch(setAddressCount(response.count));
    return response.data;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsAddressLoading(false));
  }
};

export const createAddress =
  ({ onSuccess, ...payload }: ICreateAddressPayload) =>
  async (dispatch: AppDispatch): Promise<IAddress | null> => {
    dispatch(setIsAddressLoading(true));

    try {
      const response = await addressesTransport.create(payload);
      dispatch(addAddressListItem(response));
      onSuccess?.();
      return response;
    } catch (error) {
      axiosErrorHandler(error);
      return null;
    } finally {
      dispatch(setIsAddressLoading(false));
    }
  };

export const updateAddress = (payload: IUpdateAddressPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAddressLoading(true));

  try {
    const response = await addressesTransport.update(payload);
    dispatch(patchAddressListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAddressLoading(false));
  }
};

export const deleteAddress = (payload: IDeleteAddressPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAddressLoading(true));

  try {
    const response = await addressesTransport.delete(payload);
    dispatch(removeAddressListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAddressLoading(false));
  }
};

export const updateAddressBySeller = (payload: IUpdateBySellerAddressPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAddressLoading(true));

  try {
    const response = await addressesTransport.updateBySeller(payload);
    dispatch(patchAddressListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAddressLoading(false));
  }
};

export const getAddressSuggestions = (params: IAddressSuggestionsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsAddressLoading(true));

  try {
    const response = await addressesTransport.getSuggestions(params);
    dispatch(setAddressSuggestions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAddressLoading(false));
  }
};
